import { useAsyncFn } from "react-use";
import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { TransferStatus, TransferService } from "@/domains/transfer";
// components
import { Button, Form, Input, Modal, Radio } from "antd";

interface OperatorMemberDepositCheckForm {
  status: TransferStatus;
  amount: string;
  result?: string;
  note?: string;
  transaction?: string;
}

interface CheckModalProps {
  onCheck: () => void;
}

export interface CheckModalRef {
  open: (transferId: string) => void;
}

export const CheckModal = forwardRef(
  ({ onCheck }: CheckModalProps, ref: Ref<CheckModalRef>) => {
    const [showing, setShowing] = useState(false);

    const [transferId, setTransferId] = useState<string>();

    const [form] = Form.useForm<OperatorMemberDepositCheckForm>();

    const [disabled, setDisabled] = useState(true);

    const [state, readTransfer] = useAsyncFn(async (transferId: string) => {
      const res = await TransferService.read(transferId).run();
      const transfer = res.data.msg.transfer;
      form.setFieldsValue({
        status: TransferStatus.checked_and_refused,
        amount: transfer.amount,
        result: transfer.result,
        transaction: transfer.transaction?.replaceAll(",", "\n"),
      });
    });

    const handleConfirm = async () => {
      if (!transferId) return;
      const formValue = await form.validateFields();
      await TransferService.check(transferId, {
        ...formValue,
        // 替代 textarea 的換行符號
        transaction: formValue.transaction
          ? formValue.transaction.replaceAll("\n", ",")
          : undefined,
      }).run();
      setShowing(false);
      onCheck();
    };

    const handleCancel = () => {
      setShowing(false);
    };

    useImperativeHandle(ref, () => {
      const open = async (_transferId: string) => {
        form.resetFields();
        setTransferId(_transferId);
        await readTransfer(_transferId);
        setShowing(true);
      };

      return { open };
    });

    return (
      <Modal
        title="會員代收檢核"
        open={showing}
        width={600}
        footer={[
          <div className="flex jc:end gap:8" key="footer">
            <Button key="back" onClick={handleCancel}>
              取消
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={state.loading}
              onClick={handleConfirm}
            >
              送出
            </Button>
          </div>,
        ]}
        onCancel={handleCancel}
      >
        <Form
          form={form}
          labelCol={{ span: 4 }}
          onValuesChange={(_, values) => {
            setDisabled(values.status !== TransferStatus.checked_and_allowed);
          }}
        >
          <Form.Item
            label="存單狀態"
            name="status"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={TransferStatus.checked_and_allowed}>通過</Radio>
              <Radio value={TransferStatus.checked_and_refused}>拒絕</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="存單顆數" name="amount">
            <Input readOnly type="number" />
          </Form.Item>

          <Form.Item
            label="實收顆數"
            name="result"
            rules={
              disabled ? [] : [{ required: true, message: "實收顆數不可空白" }]
            }
          >
            <Input
              type="number"
              placeholder="請輸入實收顆數"
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item label="存單備註" name="note">
            <Input placeholder="請輸入存單備註" disabled={disabled} />
          </Form.Item>

          <Form.Item
            label="交易哈希"
            name="transaction"
            extra="若存在有多筆交易，請每筆一行輸入"
          >
            <Input.TextArea
              placeholder="請輸入交易哈希"
              rows={4}
              disabled={disabled}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);
